import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { Button } from '../ui/Button';
import { formatCurrency, formatExchangeRate } from '../utils/format';
import type { Intent, QuoteSnapshot } from '../wizard/types';

interface RecommendedCardProps {
  quote: QuoteSnapshot | null;
  ready: boolean;
  /** A pre-quote request is in flight (show a skeleton instead of placeholder). */
  loading?: boolean;
  /** The estimate resolved with zero quotable options for this selection. */
  noOptions?: boolean;
  bullets: string[];
  brandName?: string;
  /**
   * Drives the perspective of the headline labels. For `request`, the user is
   * the recipient (someone else pays them), so "You pay" / "Recipient gets"
   * would be inverted — we surface "They pay" / "You receive" instead.
   */
  intent: Intent | null;
  onContinue: () => void;
}

/**
 * The "Lola Send" recommendation card. Fills progressively as the user picks
 * intent / route / funding / payout. Shows FX rate, total fee, and how much
 * the recipient will get. CTA at the bottom advances the wizard into the
 * Collect phase.
 *
 * Three visual stages:
 *   1. placeholder       → no amount yet
 *   2. quote.estimated   → partial fee/FX (funding or payout missing)
 *   3. quote complete    → final fee/FX, CTA enabled
 */
export const RecommendedCard = ({
  quote,
  ready,
  loading = false,
  noOptions = false,
  bullets,
  brandName = 'Lola Send',
  intent,
  onContinue,
}: RecommendedCardProps) => {
  const { t } = useTranslation('smart');

  const labelVariant = intent === 'request' ? 'request' : 'default';
  const youPayLabel = t(`wizard.recommended.youPay.${labelVariant}`);
  const recipientGetsLabel = t(`wizard.recommended.recipientGets.${labelVariant}`);

  return (
    <motion.section
      layout
      className="border-border bg-bg-elevated relative overflow-hidden rounded-3xl border p-5"
    >
      <div className="text-fg-muted text-[10px] font-semibold uppercase tracking-[0.2em]">
        {t('wizard.recommended.label')}
      </div>
      <h2 className="mt-2 text-2xl font-semibold tracking-tight">{brandName}</h2>
      <p className="text-fg-muted mt-1 text-sm">
        {t('wizard.recommended.tagline')}
      </p>

      {!quote ? (
        noOptions ? (
          <div className="border-border bg-bg/40 mt-5 rounded-2xl border border-dashed p-4 text-center">
            <p className="text-fg-muted text-sm">
              {t('wizard.recommended.noOptions')}
            </p>
          </div>
        ) : loading ? (
          <div
            className="border-border bg-bg/40 mt-5 animate-pulse rounded-2xl border p-4"
            aria-busy="true"
          >
            <div className="bg-fg-muted/20 h-6 w-2/3 rounded" />
            <div className="bg-fg-muted/10 mt-3 h-6 w-1/2 rounded" />
          </div>
        ) : (
          <div className="border-border bg-bg/40 mt-5 rounded-2xl border border-dashed p-4 text-center">
            <p className="text-fg-muted text-sm">
              {t('wizard.recommended.placeholder')}
            </p>
          </div>
        )
      ) : (
        <>
          {/* Headline: what the sender pays vs. what the recipient receives.
              The amount in step 2 is entered in receive currency, so
              `receiveAmount` is exactly the user's input; `sendAmount` is
              derived from the FX rate (excluding the transfer fee, which is
              broken out below).
              Surface uses the One-Tap "Primary Mesh" gradient — this is the
              flagship brand block of the wizard, so it inherits the same
              hero treatment as the home greeting. White text + white-with-
              opacity rules + a translucent divider keep it readable on top
              of the green mesh in both light and dark modes. */}
          <div className="bg-primary-mesh mt-5 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-white/80 text-sm">{youPayLabel}</span>
              <span className="text-white text-2xl font-semibold tabular-nums">
                {formatCurrency(
                  quote.sendAmount + quote.totalFee,
                  quote.sendCurrency,
                )}
              </span>
            </div>
            <div className="border-white/20 mt-3 flex items-center justify-between gap-3 border-t pt-3">
              <span className="text-white/80 text-sm">{recipientGetsLabel}</span>
              <span className="text-white text-2xl font-semibold tabular-nums">
                {formatCurrency(quote.receiveAmount, quote.receiveCurrency)}
              </span>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="border-border bg-bg/40 rounded-2xl border p-3">
              <div className="text-fg-muted text-[10px] font-semibold uppercase tracking-wide">
                {t('wizard.recommended.fxRate')}
              </div>
              <div className="text-fg mt-1 text-xl font-semibold tabular-nums">
                {formatExchangeRate(quote.exchangeRate)}
              </div>
              <div className="text-fg-muted mt-0.5 text-[11px]">
                1 {quote.sendCurrency} → {quote.receiveCurrency}
              </div>
            </div>
            <div className="border-border bg-bg/40 rounded-2xl border p-3">
              <div className="text-fg-muted text-[10px] font-semibold uppercase tracking-wide">
                {t('wizard.recommended.transferFee')}
              </div>
              <div className="text-fg mt-1 text-xl font-semibold tabular-nums">
                {formatCurrency(quote.totalFee, quote.sendCurrency)}
              </div>
              <div className="text-fg-muted mt-0.5 text-[11px]">
                {quote.estimated
                  ? t('wizard.recommended.estimated')
                  : t('wizard.recommended.flatFee')}
              </div>
            </div>
          </div>

          <p className="text-fg-muted mt-2 text-[11px]">
            {t('wizard.recommended.amountsDisclaimer')}
          </p>
        </>
      )}

      {bullets.length > 0 && (
        <ul className="mt-4 space-y-2 text-sm">
          {bullets.map((b) => (
            <li key={b} className="text-fg-muted flex items-start gap-2">
              <span className="text-accent" aria-hidden>
                ✓
              </span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5">
        <Button
          onClick={onContinue}
          disabled={!ready || noOptions}
          fullWidth
          size="lg"
        >
          {ready
            ? t('wizard.recommended.ready')
            : t('wizard.recommended.incomplete')}
          {ready && !noOptions && <span aria-hidden>→</span>}
        </Button>
      </div>
    </motion.section>
  );
};
